(() => {
  const STORAGE_KEY = 'littleNestOvulationResults';
  const MAX_SAVED_RESULTS = 10;

  const addDays = (date, days) => {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
  };

  const dateValue = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

  const parseLocalDate = (value) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T12:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatDate = (date) => date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const toInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    if (!copied) throw new Error('Copy failed');
  };

  const saveLocally = (record) => {
    let savedResults = [];

    try {
      savedResults = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (!Array.isArray(savedResults)) savedResults = [];
    } catch (error) {
      savedResults = [];
    }

    savedResults.unshift(record);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedResults.slice(0, MAX_SAVED_RESULTS)));
  };

  const initCalculator = (root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const form = root.querySelector('[data-btov-form]');
    const lastPeriodInput = root.querySelector('[data-btov-last-period]');
    const cycleLengthInput = root.querySelector('[data-btov-cycle-length]');
    const errorElement = root.querySelector('[data-btov-error]');
    const results = root.querySelector('[data-btov-results]');
    const menstruationElement = root.querySelector('[data-btov-menstruation]');
    const fertileElement = root.querySelector('[data-btov-fertile]');
    const dueElement = root.querySelector('[data-btov-due]');
    const monthElement = root.querySelector('[data-btov-calendar-month]');
    const cycleInfoElement = root.querySelector('[data-btov-cycle-info]');
    const calendarGrid = root.querySelector('[data-btov-calendar-grid]');
    const nextCycleButton = root.querySelector('[data-btov-next-cycle]');
    const resetButton = root.querySelector('[data-btov-reset]');
    const saveForm = root.querySelector('[data-btov-save-form]');
    const emailInput = root.querySelector('[data-btov-email]');
    const saveStatus = root.querySelector('[data-btov-save-status]');
    const copyButton = root.querySelector('[data-btov-copy-code]');

    let initialPeriodDate = null;
    let displayedPeriodDate = null;
    let cycleLength = 28;
    let currentCycleIndex = 1;

    lastPeriodInput.value = toInputDate(new Date());

    const generateCalendar = (periodDate, fertileStart, fertileEnd) => {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December',
      ];
      const weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
      const firstDay = new Date(periodDate.getFullYear(), periodDate.getMonth(), 1);
      const lastDay = new Date(periodDate.getFullYear(), periodDate.getMonth() + 1, 0);
      const visibleEnd = fertileEnd > lastDay ? fertileEnd : lastDay;
      const calendarEnd = addDays(visibleEnd, 6 - visibleEnd.getDay());
      const fertileStartValue = dateValue(fertileStart);
      const fertileEndValue = dateValue(fertileEnd);
      const periodValue = dateValue(periodDate);
      const spansMonths = visibleEnd.getMonth() !== periodDate.getMonth()
        || visibleEnd.getFullYear() !== periodDate.getFullYear();

      monthElement.textContent = spansMonths
        ? `${monthNames[periodDate.getMonth()]} ${periodDate.getFullYear()} - ${monthNames[visibleEnd.getMonth()]} ${visibleEnd.getFullYear()}`
        : `${monthNames[periodDate.getMonth()]} ${periodDate.getFullYear()}`;
      calendarGrid.replaceChildren();

      weekdays.forEach((weekday) => {
        const weekdayElement = document.createElement('div');
        weekdayElement.className = 'btov__weekday';
        weekdayElement.textContent = weekday;
        weekdayElement.setAttribute('aria-hidden', 'true');
        calendarGrid.appendChild(weekdayElement);
      });

      for (let index = 0; index < firstDay.getDay(); index += 1) {
        const emptyDay = document.createElement('div');
        emptyDay.className = 'btov__day btov__day--empty';
        emptyDay.setAttribute('aria-hidden', 'true');
        calendarGrid.appendChild(emptyDay);
      }

      for (let currentDate = new Date(firstDay); currentDate <= calendarEnd; currentDate = addDays(currentDate, 1)) {
        const currentValue = dateValue(currentDate);
        const dayElement = document.createElement('div');
        dayElement.className = 'btov__day';
        dayElement.textContent = String(currentDate.getDate());

        if (currentDate.getMonth() !== periodDate.getMonth()) {
          dayElement.classList.add('btov__day--adjacent');
        }

        if (currentValue === periodValue) {
          dayElement.classList.add('btov__day--period');
          dayElement.setAttribute('aria-label', `${formatDate(currentDate)}, period start`);
        } else if (currentValue >= fertileStartValue && currentValue <= fertileEndValue) {
          dayElement.classList.add('btov__day--fertile');
          dayElement.setAttribute('aria-label', `${formatDate(currentDate)}, fertile day`);
        }

        calendarGrid.appendChild(dayElement);
      }
    };

    const displayCycle = (periodDate) => {
      displayedPeriodDate = new Date(periodDate);
      const ovulationDate = addDays(periodDate, cycleLength - 14);
      const fertileStart = addDays(ovulationDate, -5);
      const fertileEnd = new Date(ovulationDate);
      const dueDate = addDays(periodDate, 280 + cycleLength - 28);

      menstruationElement.textContent = formatDate(periodDate);
      fertileElement.textContent = `${formatDate(fertileStart)} - ${formatDate(fertileEnd)}`;
      dueElement.textContent = formatDate(dueDate);
      cycleInfoElement.textContent = `Cycle ${currentCycleIndex}/6`;
      nextCycleButton.hidden = currentCycleIndex >= 6;
      results.hidden = false;
      saveStatus.textContent = '';
      saveStatus.removeAttribute('data-state');
      generateCalendar(periodDate, fertileStart, fertileEnd);
    };

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      errorElement.textContent = '';
      initialPeriodDate = parseLocalDate(lastPeriodInput.value);
      cycleLength = Number.parseInt(cycleLengthInput.value, 10);

      if (!initialPeriodDate) {
        errorElement.textContent = 'Please select the first day of your last period.';
        lastPeriodInput.focus();
        return;
      }

      if (cycleLength < 28 || cycleLength > 32) {
        errorElement.textContent = 'Please select a cycle length from 28 to 32 days.';
        cycleLengthInput.focus();
        return;
      }

      currentCycleIndex = 1;
      displayCycle(initialPeriodDate);
      results.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
    });

    nextCycleButton.addEventListener('click', () => {
      if (!initialPeriodDate || currentCycleIndex >= 6) return;
      currentCycleIndex += 1;
      displayCycle(addDays(initialPeriodDate, cycleLength * (currentCycleIndex - 1)));
    });

    resetButton.addEventListener('click', () => {
      form.reset();
      lastPeriodInput.value = toInputDate(new Date());
      results.hidden = true;
      calendarGrid.replaceChildren();
      errorElement.textContent = '';
      saveForm.reset();
      saveStatus.textContent = '';
      saveStatus.removeAttribute('data-state');
      initialPeriodDate = null;
      displayedPeriodDate = null;
      currentCycleIndex = 1;
      lastPeriodInput.focus();
    });

    saveForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const email = emailInput.value.trim();
      saveStatus.textContent = '';
      saveStatus.removeAttribute('data-state');

      if (!email || !emailInput.checkValidity()) {
        saveStatus.textContent = 'Please enter a valid email address.';
        saveStatus.dataset.state = 'error';
        emailInput.focus();
        return;
      }

      try {
        saveLocally({
          email,
          savedAt: new Date().toISOString(),
          cycle: currentCycleIndex,
          cycleLength,
          periodDate: displayedPeriodDate ? toInputDate(displayedPeriodDate) : '',
          menstruationDate: menstruationElement.textContent,
          fertileDays: fertileElement.textContent,
          dueDate: dueElement.textContent,
        });
        emailInput.value = '';
        saveStatus.textContent = 'Calculation saved successfully on this device.';
        saveStatus.dataset.state = 'success';
      } catch (error) {
        saveStatus.textContent = 'Your browser blocked local saving. Please take a screenshot of the result.';
        saveStatus.dataset.state = 'error';
      }
    });

    copyButton.addEventListener('click', async () => {
      const code = copyButton.dataset.code || '';
      const originalLabel = `Code: ${code}`;

      try {
        await copyText(code);
        copyButton.textContent = 'Copied!';
      } catch (error) {
        copyButton.textContent = 'Copy failed';
      }

      window.setTimeout(() => {
        copyButton.textContent = originalLabel;
      }, 1200);
    });
  };

  const initAll = () => {
    document.querySelectorAll('[data-baby-ovulation-full]').forEach(initCalculator);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
})();
